<template>
  <a-modal
    class="material-color-picker"
    v-model="visible"
    width="600px"
    :class="{ 'material-color-picker--filled': options.length }"
    title="素材拾色器"
  >
    <canvas
      ref="canvas"
      v-show="activeOption"
      class="material-color-picker__canvas"
      @mousemove="handleMouseMove"
      @mouseleave="handleMouseLeave"
      @click="handleClick"
    />
    <span
      class="material-color-picker__title"
      v-if="position"
      :style="{ top: position.y + 'px', left: position.x + 'px' }"
    >
      <span
        class="material-color-picker__preview"
        :style="{ backgroundColor: color }"
      ></span>
      {{ color }}
    </span>
    <a-empty
      v-show="!activeOption"
      :image="defaultImage"
      description="当前片段暂未添加素材"
    />
    <div class="material-color-picker__scroll" v-show="options.length">
      <div class="material-color-picker__images">
        <div
          v-for="op in options"
          :key="op.url"
          class="material-color-picker__image"
          :class="{
            'material-color-picker__image--active': activeOption === op.url
          }"
          @click="handleImageSelect(op)"
        >
          <div
            class="material-color-picker__image-inner"
            :style="{ backgroundImage: `url(${op.thumbnail})` }"
          />
        </div>
      </div>
    </div>
    <a-row slot="footer" type="flex" align="middle">
      <a-col class="material-color-picker__color" flex="1" v-if="selected">
        <span
          class="material-color-picker__preview"
          :style="{ backgroundColor: selected }"
        ></span>
        已选中颜色: {{ selected }}
      </a-col>
      <a-col flex="1" v-else>
        提示：请使用鼠标十字光标在所选素材上点击拾取颜色。
      </a-col>
      <a-button type="primary" :disabled="!selected" @click="done">
        完成
      </a-button>
    </a-row>
  </a-modal>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import { Empty } from 'ant-design-vue';
import { encodeURIIfNeeded, getImageAspectRatio } from '../../utils';

export default {
  data() {
    return {
      activeOption: void 0,
      position: null,
      color: '',
      selected: '',
      defaultImage: Empty.PRESENTED_IMAGE_SIMPLE
    };
  },
  computed: {
    ...mapState(['effects', 'fragments']),
    ...mapGetters(['activeFragment']),
    visible: {
      get() {
        return this.$store.state.materialColorVisible;
      },
      set(value) {
        this.$store.commit('handleMaterialColorVisible', value);
      }
    },
    options() {
      const srcLookup = {};
      function getSrc(type, src) {
        const usedSrc = encodeURIIfNeeded(src);
        return type === 'image' ? usedSrc : usedSrc + '?x-oss-process=video/snapshot,t_1000,f_jpg,w_0,m_fast';
      }
      function getThumbnail(type, src) {
        const usedSrc = encodeURIIfNeeded(src);
        if (/\?/.test(usedSrc)) {
          return usedSrc;
        }
        return usedSrc + (type === 'image' ? '?x-oss-process=image/resize,w_90' : '?x-oss-process=video/snapshot,t_1000,f_jpg,w_90,m_fast');
      }
      this.activeFragment.effects.concat(this.activeFragment.background || []).forEach(id => {
        const effect = this.effects[id];
        switch (effect.type) {
          case 'image':
          case 'video': {
            const url = getSrc(effect.type, effect.content.src);
            if (!srcLookup[url]) {
              srcLookup[url] = {
                id: effect.id,
                name: effect.content.name,
                url,
                thumbnail: getThumbnail(effect.type, effect.content.src)
              };
            }
            break;
          }
          case 'group':
          case 'container':
          case 'textContainer': {
            const prevName = effect.content.name || (effect.type === 'textContainer' && effect.content.text) || 'container or group';
            effect.children.forEach(cid => {
              const childEffect = this.effects[cid];
              if (childEffect.type === 'image' || childEffect.type === 'video') {
                const url = getSrc(childEffect.type, childEffect.content.src);
                if (!srcLookup[url]) {
                  srcLookup[url] = {
                    id: childEffect.id,
                    name: [prevName, childEffect.content.name].join('/'),
                    url,
                    thumbnail: getThumbnail(childEffect.type, childEffect.content.src)
                  };
                }
              }
            });
          }
        }
      });

      return Object.keys(srcLookup).map(k => srcLookup[k]);
    }
  },
  methods: {
    handleMouseMove(e) {
      const canvas = this.$refs.canvas;

      const bcr = canvas.getBoundingClientRect();
      const parentBcr = canvas.parentElement.getBoundingClientRect();
      const context = canvas.getContext('2d');

      const imgdata = context.getImageData(
        (e.clientX - bcr.left) * canvas.width / bcr.width,
        (e.clientY - bcr.top) * canvas.height / bcr.height,
        1, 1);
      const arr = [].slice.call(imgdata.data, 0, 3);
      this.color = '#' + arr.map(n => {
        const s = n.toString(16);
        return n < 16 ? '0' + s : s;
      }).join('');
      this.position = {
        x: e.clientX - parentBcr.left,
        y: e.clientY - parentBcr.top
      };
    },
    handleMouseLeave() {
      this.position = null;
    },
    handleClick() {
      this.selected = this.color;
    },
    draw() {
      const canvas = this.$refs.canvas;

      if (!canvas) {
        this.$nextTick(this.draw);
        return;
      }

      getImageAspectRatio(this.activeOption, img => {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const context = canvas.getContext('2d');
        context.save();
        context.fillStyle = '#fff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(img, 0, 0);
        context.restore();
      }, true);
    },
    handleImageSelect(op) {
      this.activeOption = op.url;
    },
    done() {
      this.$store.commit('handleMaterialColor', this.selected);
      setTimeout(() => {
        this.visible = false;
      }, 0);
    }
  },
  watch: {
    activeOption() {
      this.draw();
    },
    visible(val) {
      if (val) {
        this.activeOption = this.options.length ? this.options[0].url : void 0;
        this.color = '';
        this.selected = '';
        this.position = null;
      }
    }
  }
};
</script>
<style>
.material-color-picker .ant-modal-mask,
.material-color-picker .ant-modal-wrap {
  z-index: 1051;
}

.material-color-picker--filled .ant-modal-body {
  position: relative;
  background-color: #f7f8f9;
  height: 540px;
  z-index: 0;
}

.material-color-picker__canvas {
  max-width: 100%;
  max-height: 420px;
  cursor: crosshair;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 120px;
  margin: auto;
  transition: 0.3s;
}

.material-color-picker__scroll {
  position: absolute;
  bottom: 0;
  height: 120px;
  left: 0;
  width: 100%;
  background-color: #fff;
  border-top: 1px solid #e8e8e8;
  overflow-x: auto;
}

.material-color-picker__images {
  position: absolute;
  top: 0;
  left: 16px;
  bottom: 0;
  margin: auto 0;
  height: 90px;
  display: flex;
}

.material-color-picker__image {
  width: 90px;
  height: 90px;
  margin-right: 16px;
  cursor: pointer;
  position: relative;
  background: #fff
    url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjgiIHZpZXdCb3g9IjAgMCAyIDIiPjxwYXRoIGQ9Ik0wIDB2MWgydjFIMVYweiIgZmlsbD0icmdiYSgwLDAsMCwuMikiLz48L3N2Zz4=);
  background-size: 12px;
}

.material-color-picker__image-inner {
  position: absolute;
  transition: 0.3s;
  background: transparent center no-repeat;
  background-size: cover;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.material-color-picker__image--active .material-color-picker__image-inner,
.material-color-picker__image:hover .material-color-picker__image-inner {
  box-shadow: inset 0 0 0 2px #007eff;
}

.material-color-picker .ant-modal-footer {
  text-align: left;
}

.material-color-picker__title {
  position: absolute;
  left: 0;
  top: 0;
  padding: 4px 12px 4px 36px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border-radius: 2px;
  pointer-events: none;
  transform: translate(-50%);
  margin-top: 8px;
  z-index: 1;
}

.material-color-picker__title .material-color-picker__preview {
  left: 8px;
  margin-top: -10px;
  width: 20px;
  height: 20px;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.1);
}

.material-color-picker__color {
  position: relative;
  padding-left: 32px;
}

.material-color-picker__preview {
  width: 24px;
  height: 24px;
  border-radius: 99px;
  position: absolute;
  left: 0;
  top: 50%;
  margin-top: -12px;
  box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.06);
  transition: 0.2s;
}
</style>